import type { RouteObject } from "react-router-dom";
import { lazy } from "react";
import { redirect } from "react-router-dom";
import { addSlashes } from "../route.utils";
import { Spinner } from "@/components/ui/v2/spinner";
import { PerformanceIndividualReview } from "@/components/performance/individual-review";

// Lazy so the route file doesn't eagerly pull the tabs page (which imports
// nav-tabs.config) during config initialization — avoids a circular-import TDZ.
const Performance = lazy(() => import("@/pages/(home)/(my-space)/performance"));
const MsPerformanceMyReviewsPage = lazy(() =>
  import("@/pages/(home)/(my-space)/performance/reviews").then((m) => ({
    default: m.PerformanceReviews,
  })),
);
const MsPerformanceReviewRequestsPage = lazy(() =>
  import("@/pages/(home)/(my-space)/performance/review-requests").then(
    (m) => ({ default: m.PerformanceReviewRequest }),
  ),
);

export const DYNAMIC_PERFORMANCE_ID_KEY = "id";

export enum MS_PERFORMANCE_PATH_SEGMENTS {
  ROOT = "performance",
  MY_REVIEWS = "my-reviews",
  // Reused both as the root-level tab segment AND, combined with VIEW below,
  // as the per-review detail segment (`/:id/review-requests`) — same literal
  // string in two different route positions, not a collision.
  REVIEW_REQUESTS = "review-requests",
  VIEW = `:${DYNAMIC_PERFORMANCE_ID_KEY}`,
}

export const myspacePerformanceRoutes: RouteObject = {
  path: MS_PERFORMANCE_PATH_SEGMENTS.ROOT,
  handle: { breadcrumb: "Performance" },
  HydrateFallback: Spinner,
  children: [
    // ── Page-level tabs layout: each tab owns its own table ──────────────────
    {
      Component: Performance,
      handle: { flushContent: true },
      children: [
        {
          index: true,
          loader: () => redirect(MS_PERFORMANCE_PATHS.MY_REVIEWS),
        },
        {
          path: MS_PERFORMANCE_PATH_SEGMENTS.MY_REVIEWS,
          handle: { breadcrumb: "My Reviews" },
          Component: MsPerformanceMyReviewsPage,
        },
        {
          path: MS_PERFORMANCE_PATH_SEGMENTS.REVIEW_REQUESTS,
          handle: { breadcrumb: "Review Requests" },
          Component: MsPerformanceReviewRequestsPage,
        },
      ],
    },
    {
      // Single review-detail route. The old `:id` index (reusing the tabs
      // page itself) was dead — nothing ever navigated to bare `/performance/:id`,
      // only to `.REVIEW(id)` — so it's dropped rather than re-parented.
      path: `${MS_PERFORMANCE_PATH_SEGMENTS.VIEW}/${MS_PERFORMANCE_PATH_SEGMENTS.REVIEW_REQUESTS}`,
      Component: PerformanceIndividualReview,
      handle: { breadcrumb: "Performance Review" },
    },
  ],
};

export const MS_PERFORMANCE_PATHS = Object.freeze({
  ROOT: addSlashes(MS_PERFORMANCE_PATH_SEGMENTS.ROOT),
  MY_REVIEWS: addSlashes(
    MS_PERFORMANCE_PATH_SEGMENTS.ROOT,
    MS_PERFORMANCE_PATH_SEGMENTS.MY_REVIEWS,
  ),
  REVIEW_REQUESTS: addSlashes(
    MS_PERFORMANCE_PATH_SEGMENTS.ROOT,
    MS_PERFORMANCE_PATH_SEGMENTS.REVIEW_REQUESTS,
  ),
  REVIEW: (id: string | number) =>
    addSlashes(
      MS_PERFORMANCE_PATH_SEGMENTS.ROOT,
      String(id),
      MS_PERFORMANCE_PATH_SEGMENTS.REVIEW_REQUESTS,
    ),
});
